import React, { useState, useMemo } from 'react';
import { 
  Truck, 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  MapPin,
  Calendar,
  Clock,
  CheckCircle,
  AlertTriangle,
  Package,
  User,
  Building,
  Phone,
  Mail,
  Activity,
  ArrowLeft,
  BarChart3,
  TrendingUp,
  FileText,
  Navigation
} from 'lucide-react';

// Mock delivery data
const deliveries = [
  {
    id: 'delivery-001',
    delivery_number: 'DL-2025-001',
    sales_order_id: 'SO-2025-001',
    customer_id: 'cust-001',
    customer_name: '台灣電子科技股份有限公司',
    delivery_date: '2025-01-20',
    scheduled_date: '2025-01-20',
    actual_date: '2025-01-20',
    status: 'delivered',
    priority: 'high',
    delivery_method: 'truck',
    tracking_number: 'TW2025001001',
    driver_name: '王大明',
    driver_phone: '0912-345-678',
    vehicle_number: 'ABC-1234',
    delivery_address: {
      street: '台北市信義區信義路五段7號',
      city: '台北市',
      postal_code: '110',
      country: '台灣'
    },
    contact_person: {
      name: '李經理',
      phone: '02-2345-6789',
      email: 'manager.li@tetc.com.tw'
    },
    items: [
      {
        item_id: 'item-001',
        product_name: '鋁合金材料 A-grade',
        quantity: 500,
        unit: '公斤',
        delivered_quantity: 500,
        packaging: '木箱包裝'
      },
      {
        item_id: 'item-002',
        product_name: '鋁合金材料 B-grade',
        quantity: 300,
        unit: '公斤',
        delivered_quantity: 300,
        packaging: '木箱包裝'
      }
    ],
    delivery_notes: '貨物已安全送達，客戶已簽收確認',
    special_instructions: '需要叉車卸貨，請聯繫收貨人員',
    estimated_delivery_time: '10:00',
    actual_delivery_time: '10:15',
    signature_required: true,
    signature_received: true,
    proof_of_delivery: 'POD-2025-001.pdf'
  },
  {
    id: 'delivery-002',
    delivery_number: 'DL-2025-002',
    sales_order_id: 'SO-2025-002',
    customer_id: 'cust-002',
    customer_name: '創新科技股份有限公司',
    delivery_date: '2025-01-22',
    scheduled_date: '2025-01-22',
    actual_date: null,
    status: 'in_transit',
    priority: 'medium',
    delivery_method: 'truck',
    tracking_number: 'TW2025001002',
    driver_name: '陳小華',
    driver_phone: '0923-456-789',
    vehicle_number: 'DEF-5678',
    delivery_address: {
      street: '新竹市東區光復路二段101號',
      city: '新竹市',
      postal_code: '300',
      country: '台灣'
    },
    contact_person: {
      name: '張主任',
      phone: '03-5678-901',
      email: 'director.zhang@innov.com.tw'
    },
    items: [
      {
        item_id: 'item-003',
        product_name: '精密零件 Type-A',
        quantity: 100,
        unit: '件',
        delivered_quantity: 0,
        packaging: '防震包裝'
      }
    ],
    delivery_notes: '運送中，預計下午2點到達',
    special_instructions: '精密零件，請小心搬運',
    estimated_delivery_time: '14:00',
    actual_delivery_time: null,
    signature_required: true,
    signature_received: false,
    proof_of_delivery: null
  }
];

const DeliveryManagement = ({ onBack, navigationSource }) => {
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'preparing': 'bg-blue-100 text-blue-800',
      'in_transit': 'bg-purple-100 text-purple-800',
      'delivered': 'bg-green-100 text-green-800',
      'failed': 'bg-red-100 text-red-800',
      'cancelled': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': '待出貨',
      'preparing': '準備中',
      'in_transit': '運送中',
      'delivered': '已送達',
      'failed': '配送失敗',
      'cancelled': '已取消'
    };
    return labels[status] || status;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'low': 'bg-gray-100 text-gray-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'high': 'bg-red-100 text-red-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityLabel = (priority) => {
    const labels = {
      'low': '低',
      'medium': '中',
      'high': '高'
    };
    return labels[priority] || priority;
  };

  const getDeliveryMethodIcon = (method) => {
    const icons = {
      'truck': Truck,
      'air': Navigation,
      'sea': Package,
      'rail': FileText
    };
    return icons[method] || Truck;
  };

  const filteredDeliveries = useMemo(() => {
    return deliveries.filter(delivery => {
      if (statusFilter !== 'all' && delivery.status !== statusFilter) {
        return false;
      }
      if (priorityFilter !== 'all' && delivery.priority !== priorityFilter) {
        return false;
      }
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          delivery.delivery_number.toLowerCase().includes(searchLower) ||
          delivery.customer_name.toLowerCase().includes(searchLower) ||
          delivery.tracking_number.toLowerCase().includes(searchLower)
        );
      }
      return true;
    });
  }, [statusFilter, priorityFilter, searchTerm]);

  const renderWorkflowBanner = () => {
    if (navigationSource === 'sales-workflow') {
      return (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-l-4 border-purple-500 p-4 mb-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-purple-500 p-2 rounded-full">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-purple-800">來自業務流程: 銷售週期管理</h3>
                <p className="text-sm text-purple-600">階段D: 生產安排與產品交付 - 交貨管理</p>
              </div>
            </div>
            <button
              onClick={onBack}
              className="flex items-center space-x-2 bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>返回銷售週期</span>
            </button>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderDashboard = () => {
    const totalDeliveries = deliveries.length;
    const pendingDeliveries = deliveries.filter(d => d.status === 'pending').length;
    const inTransitDeliveries = deliveries.filter(d => d.status === 'in_transit').length;
    const deliveredDeliveries = deliveries.filter(d => d.status === 'delivered').length;
    const onTimeDeliveries = deliveries.filter(d => d.status === 'delivered' && d.actual_date === d.scheduled_date).length;
    const onTimeRate = deliveredDeliveries > 0 ? (onTimeDeliveries / deliveredDeliveries * 100).toFixed(1) : 0;

    return (
      <div className="space-y-6">
        {renderWorkflowBanner()}
        
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">交貨管理儀表板</h2>
          <button
            onClick={() => setActiveView('create')}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>新增配送</span>
          </button>
        </div>

        {/* 統計卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">配送總數</p>
                <p className="text-2xl font-bold text-gray-800">{totalDeliveries}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <Truck className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">運送中</p>
                <p className="text-2xl font-bold text-gray-800">{inTransitDeliveries}</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-full">
                <Navigation className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">已送達</p>
                <p className="text-2xl font-bold text-gray-800">{deliveredDeliveries}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">準時率</p>
                <p className="text-2xl font-bold text-gray-800">{onTimeRate}%</p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-full">
                <TrendingUp className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>
        </div>

        {/* 配送狀態分佈 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">配送狀態分佈</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">待出貨</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div className="bg-yellow-600 h-2 rounded-full" style={{ width: '0%' }}></div>
                  </div>
                  <span className="text-sm font-medium">{pendingDeliveries}</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">運送中</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div className="bg-purple-600 h-2 rounded-full" style={{ width: '50%' }}></div>
                  </div>
                  <span className="text-sm font-medium">{inTransitDeliveries}</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">已送達</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div className="bg-green-600 h-2 rounded-full" style={{ width: '50%' }}></div>
                  </div>
                  <span className="text-sm font-medium">{deliveredDeliveries}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">配送優先級</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">高優先級</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div className="bg-red-600 h-2 rounded-full" style={{ width: '50%' }}></div>
                  </div>
                  <span className="text-sm font-medium">1</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">中優先級</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div className="bg-yellow-600 h-2 rounded-full" style={{ width: '50%' }}></div>
                  </div>
                  <span className="text-sm font-medium">1</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">低優先級</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div className="bg-gray-600 h-2 rounded-full" style={{ width: '0%' }}></div>
                  </div>
                  <span className="text-sm font-medium">0</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 最新配送 */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">最新配送</h3>
              <button
                onClick={() => setActiveView('list')}
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                查看全部
              </button>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {deliveries.map((delivery) => (
                <div
                  key={delivery.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    setSelectedDelivery(delivery);
                    setActiveView('detail');
                  }}
                >
                  <div className="flex items-center space-x-4">
                    <div className="bg-blue-100 p-3 rounded-full">
                      <Truck className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800">{delivery.customer_name}</h4>
                      <p className="text-sm text-gray-600">{delivery.delivery_number} - {delivery.tracking_number}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="font-semibold text-gray-800">配送日期: {delivery.delivery_date}</p>
                      <p className="text-sm text-gray-600">司機: {delivery.driver_name}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(delivery.priority)}`}>
                      {getPriorityLabel(delivery.priority)}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(delivery.status)}`}>
                      {getStatusLabel(delivery.status)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderList = () => (
    <div className="space-y-6">
      {renderWorkflowBanner()}
      
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">配送列表</h2>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setActiveView('create')}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>新增配送</span>
          </button>
          <button
            onClick={() => setActiveView('dashboard')}
            className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>返回儀表板</span>
          </button>
        </div>
      </div>

      {/* 搜尋和篩選 */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="搜尋配送單號或客戶..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">所有狀態</option>
            <option value="pending">待出貨</option>
            <option value="preparing">準備中</option>
            <option value="in_transit">運送中</option>
            <option value="delivered">已送達</option>
            <option value="failed">配送失敗</option>
          </select>
          
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">所有優先級</option>
            <option value="high">高優先級</option>
            <option value="medium">中優先級</option>
            <option value="low">低優先級</option>
          </select>
        </div>
      </div>

      {/* 配送表格 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                配送資訊
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                客戶
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                配送日期
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                司機
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                優先級
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                狀態
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredDeliveries.map((delivery) => (
              <tr key={delivery.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="bg-blue-100 p-2 rounded-full mr-3">
                      <Truck className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{delivery.delivery_number}</div>
                      <div className="text-sm text-gray-500">{delivery.tracking_number}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{delivery.customer_name}</div>
                  <div className="text-sm text-gray-500">{delivery.delivery_address.city}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{delivery.delivery_date}</div>
                  <div className="text-sm text-gray-500">{delivery.estimated_delivery_time}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{delivery.driver_name}</div>
                  <div className="text-sm text-gray-500">{delivery.vehicle_number}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(delivery.priority)}`}>
                    {getPriorityLabel(delivery.priority)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(delivery.status)}`}>
                    {getStatusLabel(delivery.status)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => {
                      setSelectedDelivery(delivery);
                      setActiveView('detail');
                    }}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderDetail = () => {
    if (!selectedDelivery) return null;

    return (
      <div className="space-y-6">
        {renderWorkflowBanner()}
        
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">配送詳細資訊</h2>
          <button
            onClick={() => setActiveView('list')}
            className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>返回列表</span>
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 基本資訊 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">配送基本資訊</h3>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">配送編號:</span>
                <span className="font-medium">{selectedDelivery.delivery_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">追蹤編號:</span>
                <span className="font-medium">{selectedDelivery.tracking_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">銷售訂單:</span>
                <span className="font-medium">{selectedDelivery.sales_order_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">預定日期:</span>
                <span className="font-medium">{selectedDelivery.scheduled_date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">實際日期:</span>
                <span className="font-medium">{selectedDelivery.actual_date || '尚未送達'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">配送狀態:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedDelivery.status)}`}>
                  {getStatusLabel(selectedDelivery.status)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">優先級:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(selectedDelivery.priority)}`}>
                  {getPriorityLabel(selectedDelivery.priority)}
                </span>
              </div>
            </div>
          </div>

          {/* 客戶資訊 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">客戶資訊</h3>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">客戶名稱:</span>
                <span className="font-medium">{selectedDelivery.customer_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">聯絡人:</span>
                <span className="font-medium">{selectedDelivery.contact_person.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">聯絡電話:</span>
                <span className="font-medium">{selectedDelivery.contact_person.phone}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">電子郵件:</span>
                <span className="font-medium">{selectedDelivery.contact_person.email}</span>
              </div>
              <div className="flex flex-col space-y-1">
                <span className="text-gray-600">配送地址:</span>
                <span className="font-medium">
                  {selectedDelivery.delivery_address.street}<br />
                  {selectedDelivery.delivery_address.city} {selectedDelivery.delivery_address.postal_code}<br />
                  {selectedDelivery.delivery_address.country}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 司機資訊 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">司機與車輛資訊</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <User className="w-8 h-8 text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-600">司機姓名</p>
              <p className="text-lg font-semibold">{selectedDelivery.driver_name}</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <Phone className="w-8 h-8 text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-600">聯絡電話</p>
              <p className="text-lg font-semibold">{selectedDelivery.driver_phone}</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <Truck className="w-8 h-8 text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-600">車牌號碼</p>
              <p className="text-lg font-semibold">{selectedDelivery.vehicle_number}</p>
            </div>
          </div>
        </div>

        {/* 配送項目 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">配送項目</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    產品名稱
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    訂購數量
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    配送數量
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    包裝方式
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {selectedDelivery.items.map((item) => (
                  <tr key={item.item_id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.product_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.quantity.toLocaleString()} {item.unit}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.delivered_quantity.toLocaleString()} {item.unit}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.packaging}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 配送時間 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">配送時間</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-gray-600" />
                <span className="text-gray-600">預計送達時間</span>
              </div>
              <span className="font-medium">{selectedDelivery.estimated_delivery_time}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-gray-600">實際送達時間</span>
              </div>
              <span className="font-medium">{selectedDelivery.actual_delivery_time || '尚未送達'}</span>
            </div>
          </div>
        </div>

        {/* 特殊說明 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">配送說明</h3>
          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-gray-700 mb-2">特殊指示</h4>
              <p className="text-gray-600 bg-gray-50 p-3 rounded">{selectedDelivery.special_instructions}</p>
            </div>
            <div>
              <h4 className="font-medium text-gray-700 mb-2">配送備註</h4>
              <p className="text-gray-600 bg-gray-50 p-3 rounded">{selectedDelivery.delivery_notes}</p>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">需要簽收:</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                selectedDelivery.signature_required ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {selectedDelivery.signature_required ? '是' : '否'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">已簽收:</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                selectedDelivery.signature_received ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {selectedDelivery.signature_received ? '是' : '否'}
              </span>
            </div>
            {selectedDelivery.proof_of_delivery && (
              <div className="flex items-center justify-between">
                <span className="text-gray-600">送達證明:</span>
                <span className="font-medium text-blue-600">{selectedDelivery.proof_of_delivery}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return renderDashboard();
      case 'list':
        return renderList();
      case 'detail':
        return renderDetail();
      case 'create':
        return (
          <div className="space-y-6">
            {renderWorkflowBanner()}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">新增配送</h2>
              <p className="text-gray-600">配送新增功能開發中...</p>
              <button
                onClick={() => setActiveView('dashboard')}
                className="mt-4 flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>返回儀表板</span>
              </button>
            </div>
          </div>
        );
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {renderContent()}
      </div>
    </div>
  );
};

export default DeliveryManagement; 